// Template references in authored text (E01-S2-T4, D-04). `{collections.cases.total}` renders a
// number; `{fields.exceptionType.value}` renders an enum value. Nothing else may be interpolated:
// never a token, a masked field, a structural value or a free-text length beyond what D-04 allows.
// No escape hatch: no nested braces, no expressions, no formatting directives.
import { parseRef, resolveRef } from "./refs.js";
const BRACE_RE = /\{([^{}]*)\}/g;
/** Coerce to a string without ever throwing (an object with no prototype makes String() throw). */
const text = (v) => {
    try {
        return typeof v === "string" ? v : String(v);
    }
    catch {
        return "";
    }
};
/** Every `{...}` in the text, in order, as written (without the braces). */
export function listTemplateRefs(input) {
    const out = [];
    for (const m of text(input).matchAll(BRACE_RE))
        out.push(m[1] ?? ""); // the group always takes part
    return out;
}
/** Placeholders that are not frame paths and are supplied by the caller (note templates: D-63, D-80). */
export const TEMPLATE_EXTRAS = ["user.id", "walkthrough.ref"];
/**
 * Is this path allowed in authored text? Numbers (counts, lengths) and enum values only.
 * Returns the reason when it is not.
 */
export function templatePathAllowed(path) {
    if (typeof path !== "string" || path.startsWith("$"))
        return { ok: false, reason: "a template path has no leading $ (D-04)" };
    const p = parseRef(path);
    if (!p)
        return { ok: false, reason: "not an allow-listed path" };
    switch (p.root) {
        case "overlays.count":
            return { ok: true };
        case "collections":
            return { ok: true };
        case "screen.context":
            return { ok: true };
        case "fields":
            if (p.prop === "value" || p.prop === "length")
                return { ok: true };
            return {
                ok: false,
                reason: `fields.<id>.${p.prop} is not a number or an enum value a template may show`,
            };
        default:
            return { ok: false, reason: `${path} is not a number or an enum value` };
    }
}
/** For the pack build: every placeholder must be an allowed path or a declared extra. */
export function checkTemplateRefs(input, extras = TEMPLATE_EXTRAS) {
    const errors = [];
    const s = text(input);
    if (/\{[^{}]*\{/.test(s) || (s.match(/\{/g) ?? []).length !== (s.match(/\}/g) ?? []).length) {
        errors.push({
            code: "template.malformed",
            ref: "",
            message: "braces must be balanced and not nested",
        });
    }
    for (const ref of listTemplateRefs(s)) {
        if (extras.includes(ref))
            continue;
        const a = templatePathAllowed(ref);
        if (!a.ok)
            errors.push({ code: "template.unknown-ref", ref, message: `{${ref}}: ${a.reason}` });
    }
    return errors;
}
/**
 * Render the text against a frame. Every placeholder must resolve to a number or an enum value
 * (or be supplied in `extras`); otherwise the result is `ok: false` with the paths that did not,
 * and no text — never a brace, the word undefined, or an object rendered as a string.
 */
export function renderTemplate(input, frame, extras = {}) {
    try {
        return render(input, frame, extras);
    }
    catch {
        return { ok: false, missing: ["(unreadable)"] };
    }
}
const FORBIDDEN = new Set(["__proto__", "constructor", "prototype"]);
const ownOf = (o, k) => typeof o === "object" &&
    o !== null &&
    !Array.isArray(o) &&
    !FORBIDDEN.has(k) &&
    Object.prototype.hasOwnProperty.call(o, k)
    ? o[k]
    : undefined;
function render(input, frame, extras) {
    if (typeof input !== "string")
        return { ok: false, missing: ["(not text)"] }; // never render something that is not authored text
    const missing = [];
    const s = text(input);
    if (checkTemplateRefs(s, Object.keys(extras)).some((e) => e.code === "template.malformed"))
        return { ok: false, missing: ["(malformed)"] };
    const out = s.replace(BRACE_RE, (_m, ref) => {
        const extra = Object.prototype.hasOwnProperty.call(extras, ref)
            ? extras[ref]
            : undefined;
        if (typeof extra === "string")
            return extra;
        const allowed = templatePathAllowed(ref);
        if (!allowed.ok) {
            missing.push(ref);
            return "";
        }
        const v = resolveRef(ref, frame);
        const p = parseRef(ref);
        if (p?.root === "fields" && p.prop === "value") {
            // enum values only (D-04): a structural value is not interpolated even though gt/eq may read it
            const cls = ownOf(ownOf(ownOf(frame, "fields"), p.id), "cls");
            if (cls !== "enum") {
                missing.push(ref);
                return "";
            }
        }
        if (typeof v === "number" && Number.isFinite(v))
            return String(v);
        if (typeof v === "string" && v.length > 0)
            return v;
        missing.push(ref);
        return "";
    });
    if (missing.length)
        return { ok: false, missing };
    if (out.includes("{") || out.includes("}"))
        return { ok: false, missing: ["(malformed)"] }; // a stray brace, whatever its order
    return { ok: true, text: out };
}
//# sourceMappingURL=template.js.map